'use client';

import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { getUserOrganizations, Organization } from '@/lib/api/organizations';

interface OrganizationContextType {
  organizations: Organization[];
  selectedOrg: Organization | null;
  selectOrganization: (orgId: string) => void;
  refreshOrganizations: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const OrganizationContext = createContext<OrganizationContextType | null>(null);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const authContext = useContext(AuthContext);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch organizations when user is available
  const fetchOrganizations = async () => {
    if (!authContext?.user || !authContext?.token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getUserOrganizations(authContext.user.id, authContext.token);
      
      if (response.success && response.data) {
        const orgs = Array.isArray(response.data) ? response.data : [response.data];
        setOrganizations(orgs);
        
        // Auto-select organization
        const savedOrgId = localStorage.getItem('selectedOrgId');
        
        if (savedOrgId && orgs.find((org: Organization) => org.id === savedOrgId)) {
          // Use saved org if it exists
          setSelectedOrg(orgs.find((org: Organization) => org.id === savedOrgId) || null);
        } else if (orgs.length === 1) {
          // Auto-select if only one org
          setSelectedOrg(orgs[0]);
          localStorage.setItem('selectedOrgId', orgs[0].id);
        } else if (orgs.length > 1) {
          // Select first org by default
          setSelectedOrg(orgs[0]);
          localStorage.setItem('selectedOrgId', orgs[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch organizations');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (authContext?.user) {
      fetchOrganizations();
    } else {
      setIsLoading(false);
    }
  }, [authContext?.user]);

  const selectOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setSelectedOrg(org);
      localStorage.setItem('selectedOrgId', orgId);
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        selectedOrg,
        selectOrganization,
        refreshOrganizations,
        isLoading,
        error,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

